import React, { useRef } from 'react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import imageCompression from 'browser-image-compression';
import { imageToBase64 } from '../../../utils/receiptAnalysis';

const ImageUploader = ({
  onImageSelected = () => { },
  onManualEntry = () => { },
  onSessionReset = () => { },
  isAnalyzing = false
}) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 🧹 Limpiar sesión anterior al seleccionar nueva imagen
    console.log('🧹 Limpiando sesión anterior (selección de archivo)...');
    onSessionReset();

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('La imagen es demasiado grande. Máximo 10MB');
      return;
    }

    // URL de Supabase (se obtiene de las variables de entorno)
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    };

    try {
      console.log('Original file size:', (file.size / (1024 * 1024)).toFixed(2), 'MB');

      const compressedFile = await imageCompression(file, options);
      console.log('Compressed file size:', (compressedFile.size / (1024 * 1024)).toFixed(2), 'MB');

      const base64Image = await imageToBase64(compressedFile);

      // Mostrar info al usuario
      const originalMB = (file.size / (1024 * 1024)).toFixed(2);
      const compressedMB = (compressedFile.size / (1024 * 1024)).toFixed(2);
      alert(`Imagen optimizada: ${originalMB}MB ➔ ${compressedMB}MB`);

      onImageSelected(base64Image);
    } catch (error) {
      console.error('Error compressing image:', error);
      alert('Error al procesar la imagen. Intenta de nuevo.');
    }
  };

  const handleCapture = async () => {
    // 🧹 Limpiar sesión anterior al iniciar captura
    console.log('🧹 Limpiando sesión anterior (captura de imagen)...');
    onSessionReset();

    // Si estamos en un modo nativo (APK), usamos el plugin de Capacitor
    if (Capacitor.isNativePlatform()) {
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Uri, // Cambiamos a Uri para poder comprimir el archivo real
          source: CameraSource.Prompt,
          promptLabelHeader: 'Seleccionar imagen',
          promptLabelPhoto: 'Elegir de la galería',
          promptLabelPicture: 'Hacer una foto'
        });

        if (image && image.webPath) {
          // Obtener el archivo desde la URI de Capacitor
          const response = await fetch(image.webPath);
          const blob = await response.blob();
          const file = new File([blob], `receipt.${image.format}`, { type: `image/${image.format}` });

          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true
          };

          console.log('Original photo size:', (file.size / (1024 * 1024)).toFixed(2), 'MB');
          const compressedFile = await imageCompression(file, options);
          console.log('Compressed photo size:', (compressedFile.size / (1024 * 1024)).toFixed(2), 'MB');

          const base64Image = await imageToBase64(compressedFile);

          const originalMB = (file.size / (1024 * 1024)).toFixed(2);
          const compressedMB = (compressedFile.size / (1024 * 1024)).toFixed(2);
          alert(`Foto optimizada: ${originalMB}MB ➔ ${compressedMB}MB`);

          onImageSelected(base64Image);
        }
      } catch (error) {
        console.error('Error al capturar imagen con Capacitor:', error);
      }
    } else {
      // En entorno Web/PWA, seguimos con el comportamiento estándar
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isAnalyzing}
      />

      <div className="bg-surface border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4 group-hover:bg-primary/20 transition-colors">
          <Icon name="Camera" size={32} className="text-primary" />
        </div>

        <h3 className="text-lg font-semibold text-foreground mb-2">
          Escanear Ticket
        </h3>

        <p className="text-sm text-text-secondary mb-6 max-w-sm mx-auto">
          Toma una foto clara del ticket para que la IA pueda leer los artículos automáticamente
        </p>

        <div className="flex flex-col items-center gap-4">
          <Button
            onClick={handleCapture}
            disabled={isAnalyzing}
            className="w-full max-w-xs shadow-md"
            iconName="Camera"
            iconPosition="left"
            iconSize={20}
          >
            Tomar foto o elegir de galería
          </Button>

          <div className="flex items-center gap-2 w-full max-w-xs">
            <div className="h-px bg-border flex-1"></div>
            <span className="text-[10px] uppercase tracking-wider text-text-secondary font-bold">O también</span>
            <div className="h-px bg-border flex-1"></div>
          </div>

          <Button
            variant="outline"
            onClick={() => onManualEntry()}
            disabled={isAnalyzing}
            iconName="Edit2"
            iconSize={14}
            className="w-full max-w-xs"
          >
            Introducir manualmente
          </Button>
        </div>

        <div className="mt-8 pt-4 border-t border-border flex justify-center gap-4">
          <div className="flex items-center gap-1.5 grayscale opacity-60">
            <Icon name="Image" size={14} />
            <span className="text-xs">Galería</span>
          </div>
          <div className="flex items-center gap-1.5 grayscale opacity-60">
            <Icon name="Zap" size={14} />
            <span className="text-xs">Escaneo IA</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
